// DrillView — unified entry point for "刷题" (Session 19AW Phase 4).
//
// Per陛下: 单一刷题入口；具体题型入口放在 chapter/topic 详情、错题本里。
// This view is the sidebar's single drill entry. It's a chooser:
//   - Lists the drill-class item kinds the current course actually has
//     (MCQ / TF / 推导证明 / 简答计算 / 阅读理解 / 论述 essay).
//   - Optional scope filter: chapter selector → topic selector.
//   - "开始" button → navigates to the kind-appropriate per-kind view with
//     ?case= / ?topic= query params.
//
// Why "chooser then redirect" instead of "embed per-kind view inline":
//   The 6 per-kind views are mature standalone components with their own
//   setup/run/finish state machines. Folding them into a shared queue
//   runner is high risk for low marginal user value at this stage. The
//   chooser collapses 5 sidebar entries → 1 (the user-facing simplification
//   陛下 asked for) without rewriting 6 views. A future Phase 4.5 may unify
//   the run loop itself; that's tracked in TECH_DEBT §17 [DEBT-AW-DRILL].
//
// Flashcards intentionally NOT included here — flashcards is a study mode
// (SRS), conceptually distinct from drill (test yourself). Sidebar keeps
// flashcards as its own entry.

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ListChecks, CheckSquare, Sigma, PenLine, FileText, ScrollText, Sparkles,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useT } from '../i18n/index.js';
import type { DictionaryKey } from '@examforge/shared/i18n';
import { StateCard } from '../components/state-card.js';
// Isomorphism Phase 3: Zustand primary (replacing Context).
import { useStoreCourseData } from '../lib/hooks/use-store-course.js';

interface DrillKindSpec {
  /** Lowercase id used in URLs (`?kind=...`). */
  id: 'mcq' | 'tf' | 'proof_drill' | 'free_response' | 'reading_passage' | 'essay';
  /** Per-kind available-count predicate. */
  count: (pkg: import('@examforge/shared/schemas').IngestPackage) => number;
  /** Target route + query for "开始 →" button. */
  routeFor: (scope: { caseId?: string; topicId?: string }) => string;
  icon: ReactNode;
  labelKey: DictionaryKey;
}

const DRILL_KINDS: readonly DrillKindSpec[] = [
  {
    id: 'mcq',
    count: (pkg) => pkg.mcqs.length,
    routeFor: ({ caseId, topicId }) => `../quiz${queryString({ case: caseId, topic: topicId })}`,
    icon: <ListChecks size={16} aria-hidden="true" />,
    labelKey: 'nav.quiz',
  },
  {
    id: 'tf',
    count: (pkg) => pkg.trueFalse?.length ?? 0,
    // 19BI: TF now uses dedicated ?kind=tf rendering in quiz-view.
    routeFor: ({ caseId, topicId }) => `../quiz${queryString({ case: caseId, topic: topicId, kind: 'tf' })}`,
    icon: <CheckSquare size={16} aria-hidden="true" />,
    labelKey: 'dashboard.itemKindTf',
  },
  {
    id: 'proof_drill',
    count: (pkg) => pkg.proofDrills?.length ?? 0,
    // proof-drills view doesn't honor scope params today — passes the chapter
    // through the URL anyway so when it learns to filter, this just works.
    routeFor: ({ caseId, topicId }) => `../proof-drills${queryString({ case: caseId, topic: topicId })}`,
    icon: <Sigma size={16} aria-hidden="true" />,
    labelKey: 'nav.proofDrill',
  },
  {
    id: 'free_response',
    count: (pkg) => pkg.freeResponses?.length ?? 0,
    routeFor: ({ caseId, topicId }) => `../free-response${queryString({ case: caseId, topic: topicId })}`,
    icon: <PenLine size={16} aria-hidden="true" />,
    labelKey: 'nav.freeResponse',
  },
  {
    id: 'reading_passage',
    count: (pkg) => pkg.readingPassages?.length ?? 0,
    routeFor: ({ caseId, topicId }) => `../reading${queryString({ case: caseId, topic: topicId })}`,
    icon: <FileText size={16} aria-hidden="true" />,
    labelKey: 'nav.readingPassage',
  },
  {
    id: 'essay',
    count: (pkg) => pkg.essays?.length ?? 0,
    routeFor: ({ caseId, topicId }) => `../essay${queryString({ case: caseId, topic: topicId })}`,
    icon: <ScrollText size={16} aria-hidden="true" />,
    labelKey: 'nav.essay',
  },
];

/** Build a `?a=...&b=...` string with proper encoding; returns '' when no
 *  truthy params remain. Stable order so URLs are cache/test friendly. */
export function queryString(parts: Record<string, string | undefined>): string {
  const entries = Object.entries(parts).filter(([, v]) => typeof v === 'string' && v.length > 0);
  if (entries.length === 0) return '';
  return '?' + entries
    .map(([k, v]) => `${k}=${encodeURIComponent(v as string)}`)
    .join('&');
}

export function DrillView() {
  const t = useT();
  const navigate = useNavigate();
  const { pkg } = useStoreCourseData();

  const availableKinds = useMemo(
    () => DRILL_KINDS.filter((k) => k.count(pkg) > 0),
    [pkg],
  );

  const [selectedKind, setSelectedKind] = useState<DrillKindSpec['id'] | null>(
    availableKinds.length === 1 ? availableKinds[0]!.id : null,
  );
  const [selectedCase, setSelectedCase] = useState<string>('all');

  const start = (): void => {
    if (!selectedKind) return;
    const spec = DRILL_KINDS.find((k) => k.id === selectedKind);
    if (!spec) return;
    const target = spec.routeFor({
      ...(selectedCase !== 'all' ? { caseId: selectedCase } : {}),
    });
    navigate(target);
  };

  if (availableKinds.length === 0) {
    return (
      <StateCard
        variant="empty"
        icon={<Sparkles size={48} />}
        title={t('drill.empty')}
        hint={t('drill.emptyHint')}
      />
    );
  }

  return (
    <div className="drill-view">
      <header className="drill-header">
        <h1>{t('drill.title')}</h1>
        <p className="drill-subtitle">{t('drill.subtitle')}</p>
      </header>

      <section className="drill-section">
        <h2 className="drill-section-title">{t('drill.kindLabel')}</h2>
        <div className="drill-kind-grid" role="radiogroup" aria-label={t('drill.kindLabel')}>
          {availableKinds.map((spec) => {
            const isSelected = selectedKind === spec.id;
            return (
              <button
                key={spec.id}
                type="button"
                role="radio"
                aria-checked={isSelected}
                className={`drill-kind-card${isSelected ? ' is-selected' : ''}`}
                onClick={() => setSelectedKind(spec.id)}
                data-kind={spec.id}
              >
                <span className="drill-kind-icon">{spec.icon}</span>
                <span className="drill-kind-label">{t(spec.labelKey)}</span>
                <span className="drill-kind-count">{spec.count(pkg)}</span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="drill-section">
        <h2 className="drill-section-title">{t('drill.scopeLabel')}</h2>
        <select
          className="drill-scope-select"
          value={selectedCase}
          onChange={(e) => setSelectedCase(e.target.value)}
          aria-label={t('drill.scopeLabel')}
        >
          <option value="all">{t('drill.scopeAll')}</option>
          {pkg.cases.map((c) => (
            <option key={c.id} value={c.id}>{c.title}</option>
          ))}
        </select>
      </section>

      <div className="drill-actions">
        <button
          type="button"
          className="btn-primary drill-start"
          onClick={start}
          disabled={!selectedKind}
        >
          {t('drill.start')}
        </button>
      </div>
    </div>
  );
}
